import React, { useCallback, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useDate from '../../../../hooks/useDate/useDate';
import usePortalConfig from '../../../../hooks/usePortalConfig/usePortalConfig';
import useRolePermissions from '../../../../hooks/useRolePermissions/useRolePermissions';
import SimStatusTag from '../../../../components/StatusTag/SimStatusTag';
import TruncatedTooltip from '../../../../components/TruncatedTooltip/TruncatedTooltip';
import { Header, Row } from '../../../../components/Table/TableModels';
import { SimStatusSortPreference } from '../../../../configs/PortalConfig';
import { Sort } from '../../../../models/CommonModels';
import {
  CancelScheduledOperationRequest,
  GetDeviceByIdFn,
  GetDeviceFn,
  Sim,
  SimUpdateRequest,
} from '../../../../models/UserDevicesModels';
import UserDetailsSimRowOptions from './UserDetailsSimRowOptions';
import UserDetailsSimRowEdit from './UserDetailsSimRowEdit';
import Tooltip from '../../../../components/Tooltip/Tooltip';

function useUserDetailsSimsTable(
  sims: Sim[],
  sort: Sort,
  resendNotification: (deviceId: string) => void,
  isPerformingAction: boolean,
  getDeviceById: GetDeviceFn | GetDeviceByIdFn,
  onNewConnectionSuccess: () => void,
  onUpgradeSuccess: () => void,
  onSuspendSimSuccess: () => void,
  onRestoreConnectionSuccess: () => void,
  onTerminateConnectionSuccess: () => void,
  updateSim: (req: SimUpdateRequest, errorCallback: () => void) => void,
  cancelScheduledOperation: (req: CancelScheduledOperationRequest) => void,
) {
  const [t] = useTranslation();
  const { localeDateFormat } = useDate();
  const { isUserAllowed } = useRolePermissions();
  const { routePermissions } = usePortalConfig();
  const theme = useTheme();
  const [openRowId, setOpenRowId] = useState<string | null>(null);

  /**
   * Checks if the options button is needs to be rendered or not
   */
  const canRenderOptions = (sim: Sim) =>
    !(sim.type === 'PSIM' && !sim.phoneNumber) &&
    sim.status !== 'PENDING' &&
    sim.status !== 'TERMINATED' &&
    sim.status !== 'PROCESSING';

  /**
   * Checks if the edit button can be rendered or not
   */
  const canRenderEdit = (sim: Sim) =>
    isUserAllowed(routePermissions.editDevices) &&
    sim.status !== 'PENDING' &&
    sim.status !== 'PROCESSING' &&
    !(sim.status === 'SUSPENDED' && sim.journey === 'TERMINATION');

  /**
   * Sorts the array of sims
   */
  const sortBy = useCallback(
    (list: Sim[]): Sim[] => {
      if (list.length) {
        if (!Object.keys(list[0]).includes(sort.orderBy)) {
          throw new Error(`Invalid field name: ${sort.orderBy}`);
        }
        type SortableKeys = Extract<
          'iccid' | 'eid' | 'model' | 'phoneNumber' | 'type' | 'startDate' | 'endDate' | 'status',
          keyof Sim
        >;
        return list.sort((a, b) => {
          let comparison = 0;
          if (sort.orderBy !== 'status') {
            try {
              comparison = a[sort.orderBy as SortableKeys].localeCompare(b[sort.orderBy as SortableKeys]);
            } catch (e) {
              comparison = 1;
            }
          } else {
            const indexA = SimStatusSortPreference.indexOf(a.status);
            const indexB = SimStatusSortPreference.indexOf(b.status);
            comparison = indexA - indexB;
          }
          return sort.order === 'desc' ? -comparison : comparison;
        });
      }
      return [];
    },
    [sort.order, sort.orderBy],
  );

  const headers: Header[] = useMemo(
    () => [
      {
        id: 'iccid',
        label: t('pages.userDetails.tabs.simsTable.headers.iccid'),
        isSortable: true,
      },
      {
        id: 'eid',
        label: t('pages.userDetails.tabs.simsTable.headers.eid'),
        isSortable: true,
      },
      {
        id: 'model',
        label: t('pages.userDetails.tabs.devicesTable.headers.device'),
        isSortable: true,
      },
      {
        id: 'phoneNumber',
        label: t('pages.userDetails.tabs.simsTable.headers.mobileNumber'),
        isSortable: true,
      },
      {
        id: 'type',
        label: t('translation:pages.userDetails.tabs.simsTable.headers.simType'),
        isSortable: true,
      },
      {
        id: 'imei',
        label: t('translation:pages.userDetails.tabs.simsTable.headers.imei'),
        isSortable: true,
      },
      {
        id: 'startDate',
        label: (
          <Tooltip
            label={t('pages.userDetails.tabs.simsTable.headers.startDate')}
            message={t('pages.userDetails.tabs.simsTable.tooltips.startDate')}
          />
        ),
        isSortable: true,
      },
      {
        id: 'endDate',
        label: (
          <Tooltip
            label={t('pages.userDetails.tabs.simsTable.headers.endDate')}
            message={t('pages.userDetails.tabs.simsTable.tooltips.endDate')}
          />
        ),
        isSortable: true,
      },
      {
        id: 'status',
        label: t('pages.userDetails.tabs.simsTable.headers.status'),
        isSortable: true,
      },
      {
        id: 'edit',
        label: '',
        sx: {
          position: 'sticky',
          right: '3rem',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 1,
            boxShadow: '-8px 0px 16px 0px #00000029',
            opacity: 'var(--sticky-actions-shadow, 0)',
            pointerEvents: 'none',
            transition: 'opacity 120ms ease',
          },
        },
      },
      {
        id: 'options',
        label: '',
        sx: {
          position: 'sticky',
          right: 0,
        },
      },
    ],
    [t],
  );

  const rows: Row[] = useMemo(
    () =>
      sortBy(sims).map((s, index) => {
        const device = getDeviceById(s.deviceId);
        // True when the row ID is the same to the row with active drawer
        const isMenuOpen = openRowId === s.id;

        return {
          id: s.id,
          cells: [
            {
              value: s.iccid ? <TruncatedTooltip label={s.iccid} limit={4} /> : '',
            },
            {
              value: s.eid ? <TruncatedTooltip label={s.eid} limit={4} /> : '',
            },
            {
              value: device ? device.model : '',
            },
            {
              value: s.phoneNumber,
            },
            {
              value: s.type ? t(`common.simType.${s.type}`) : t('common.na'),
            },
            {
              value: s.imei ? <TruncatedTooltip label={s.imei} limit={4} /> : '',
            },
            {
              value: s.startDate ? DateTime.fromISO(s.startDate).toFormat(localeDateFormat) : '',
            },
            {
              value: s.endDate ? DateTime.fromISO(s.endDate).toFormat(localeDateFormat) : '',
            },
            {
              value: <SimStatusTag sim={s} label={t(`common.simStatus.${s.status}`)} />,
            },
            {
              value: canRenderEdit(s) ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    minWidth: '48px',
                  }}
                >
                  <UserDetailsSimRowEdit sim={s} updateSim={updateSim} />
                </Box>
              ) : (
                <div />
              ),
              sx: {
                position: 'sticky',
                right: '3rem',
                p: 0,
                zIndex: 1,
                backgroundColor: index % 2 !== 0 ? theme.palette.monochrome.monoChrome2 : 'white',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: 1,
                  boxShadow: '-8px 0px 16px 0px #00000029',
                  opacity: 'var(--sticky-actions-shadow, 0)',
                  pointerEvents: 'none',
                  transition: 'opacity 120ms ease',
                },
              },
            },
            {
              value: canRenderOptions(s) ? (
                <UserDetailsSimRowOptions
                  sim={s}
                  resendNotification={resendNotification}
                  isPerformingAction={isPerformingAction}
                  getDeviceById={getDeviceById}
                  onNewConnectionSuccess={onNewConnectionSuccess}
                  onUpgradeSuccess={onUpgradeSuccess}
                  onSuspendSimSuccess={onSuspendSimSuccess}
                  onRestoreConnectionSuccess={onRestoreConnectionSuccess}
                  onTerminateConnectionSuccess={onTerminateConnectionSuccess}
                  cancelScheduledOperation={cancelScheduledOperation}
                  // Keep the active row z-index on 9999 only while one of its side drawers is open.
                  onToggleDrawer={(isDrawerOpen) =>
                    setOpenRowId((prev) => {
                      if (isDrawerOpen) {
                        return s.id;
                      }

                      return prev === s.id ? null : prev;
                    })
                  }
                />
              ) : (
                <div />
              ),
              sx: {
                position: 'sticky',
                right: 0,
                placeItems: 'center',
                p: 0,
                minWidth: '48px',
                // z-index 1150 on the cell with active drawer
                zIndex: isMenuOpen ? 1150 : 1,
                backgroundColor: index % 2 !== 0 ? theme.palette.monochrome.monoChrome2 : 'white',
              },
            },
          ],
        };
      }),
    [sims, sortBy, openRowId, t, isPerformingAction],
  );

  return { headers, rows, totalSims: sims.length };
}

export default useUserDetailsSimsTable;
